import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';

import { Button, Card, Row, Col, Flex } from '@app/ui';
import pacingActions from '../actions/pacing';
import Tooltip from '@app/ui/sports-components/Tooltip';

import { PACING_CREATE_GOALS } from '../constants/config';
import './pacing.scss';
import Highlight from './Highlight';
import Table from './Table';
import Provider from './Context';
import PacingVisualization from './PacingVisualization';

const Pacing = ({ client, getGoals, getLimit, pacing }) => {
    const { t } = useTranslation('tools');
    const navigate = useNavigate();
    const canCreateGoals = useMemo(
        () => client.permissions.includes(PACING_CREATE_GOALS),
        [client],
    );

    const { isLoadingLimit, limit, availableGoalCount } = pacing;

    const showLimitInfo = !isLoadingLimit && !!limit;

    const limitInfo = useMemo(() => {
        if (!limit) return '';
        return t('pacing.alerts.goalLimitTooltip', {
            availableGoalCount,
            limit,
        });
    }, [availableGoalCount, limit, t]);

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([getGoals(), getLimit()]);
        };
        fetchData();
    }, []);

    const handleCreateGoalOnClick = useCallback(() => {
        if (Number.isFinite(limit)) {
            if (availableGoalCount <= 0) {
                const message = t('pacing.alerts.goalLimitReached', {
                    limit,
                });
                alert(message);
                return;
            }
            if (availableGoalCount <= 5) {
                const message = t('pacing.alerts.goalLimitNearing', {
                    availableGoalCount,
                    limit,
                });
                alert(message);
            }
        }

        navigate(`/c/${client.id}/pacing/create`);
    }, [availableGoalCount, client.id, limit, navigate, t]);

    return (
        <Provider>
            <Flex vertical className="gds-layout__container -m-t-4 -m-t-3-sm">
                <Row gutter={16}>
                    <Col md={18}>
                        <div className="gds-flex gds-flex--justify-between gds-flex--align-center -m-b-3">
                            <h1
                                data-test="title"
                                className="gds-text--header-md"
                            >
                                {t('pacing.pageTitle')}
                            </h1>
                        </div>
                    </Col>
                    <Col md={24}>
                        <Highlight />
                    </Col>
                    <Col md={24}>
                        {canCreateGoals && (
                            <div className="-create-goal-button-container">
                                {showLimitInfo && (
                                    <div className="-inline-block">
                                        <Tooltip
                                            text={limitInfo}
                                            position="left"
                                        >
                                            <i className="fas fa-info-circle -m-r-1 -inline" />
                                        </Tooltip>
                                    </div>
                                )}
                                <div className="-inline-block">
                                    <Button
                                        className="-create-goal-button -m-b-2"
                                        onClick={handleCreateGoalOnClick}
                                        size="large"
                                        variant="primary"
                                    >
                                        {t('pacing.buttons.createGoal')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        {' '}
                        <PacingVisualization />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Card className="-m-b-6">
                            <Table />
                        </Card>
                    </Col>
                </Row>
            </Flex>
        </Provider>
    );
};

Pacing.propTypes = {
    client: PropTypes.object,
    getGoals: PropTypes.func,
    getLimit: PropTypes.func,
    pacing: PropTypes.object,
};

export default connect(['client', 'pacing'], pacingActions)(Pacing);
